import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import { IAttachment } from 'app/features/conversations/Components/MessageEditor/Uploader';
import {
  IDocument,
  useDirectoryNodeFormMutation,
} from 'app/services/documents';
import {
  TicketCategories,
  useSubmitTicketMutation,
} from 'app/services/tickets';
import MultiFileUploader, {
  MultiFileUploaderRef,
} from 'Components/Buttons/MultiFileUploader';
import { TextAreaBasic } from 'Components/Form/Basic/TextAreaBasic';
import { TextInputBasic } from 'Components/Form/Basic/TextInputBasic';
import ErrorMessageStyle from 'Components/Form/ErrorMessageStyle';
import Label from 'Components/Form/Label';
import SelectSearchDropDown from 'Components/Form/SearchDropDown/SelectSearchDropDown';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import { useClosePopup } from 'Components/Popups';
import Spinner from 'Components/Spinner';
import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  title: Yup.string().trim().required('required'),
  description: Yup.string().trim().required('required'),
  category: Yup.object().required('required'),
});

interface ReportProblemForm {
  title: string;
  description: string;
  category: { id: string; name: string };
}

export default function SubmitTicketModal() {
  let {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    getValues,
    watch,
  } = useForm<ReportProblemForm>({
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
  });
  const closePopup = useClosePopup();
  const uploaderRef = useRef<MultiFileUploaderRef>(null);

  let [uploadDocument, { isLoading: isUploadingDocuments }] =
    useDirectoryNodeFormMutation();
  const [reportAProblem, { isLoading }] = useSubmitTicketMutation();

  const uploadDocumentHandle = (
    attachment: IAttachment,
  ): (() => Promise<IDocument>) => {
    return () =>
      uploadDocument({
        title: attachment.staged.name,
        file: attachment.staged,
        doctype: 'Other',
        description: '',
        object_type: 'document',
      }).unwrap();
  };

  const onSubmit = async (formValues: ReportProblemForm) => {
    if (!uploaderRef.current || !uploaderRef.current.upload) return;

    const uploadDocumentsPromise =
      uploaderRef.current.upload(uploadDocumentHandle);

    const reportAProblemPromise = uploadDocumentsPromise.then((documents) => {
      // return Promise.resolve;
      return reportAProblem({
        description: formValues.description,
        title: formValues.title,
        category: formValues.category.id,
        document_ids: documents.map((d) => d.id),
      }).unwrap();
    });

    Promise.all([reportAProblemPromise, uploadDocumentsPromise])
      .then(([task, documents]) => {
        closePopup();
        successToast('Ticket submitted');
      })
      .catch((e) => {
        errorToast("Ticket Couldn't be submitted");
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <OverlayWindow
        title="Submit a Ticket"
        subTitle="Send your problem and our team will contact you soon"
        onClose={closePopup}
        size={WindowSize.md}
        body={
          <div className="grid grid-cols-1 gap-4">
            <TextInputBasic
              register={register}
              maxLength={40}
              label="Title"
              placeholder="Wtrite a title..."
              errors={errors}
              type="text"
              name="title"
            />
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Label label="Category">
                  <SelectSearchDropDown
                    options={TicketCategories}
                    value={field.value}
                    onChange={field.onChange}
                    searchFilter={(search, options) =>
                      options.filter((x) => x.name.includes(search))
                    }
                    selectedItemSkin={(category) => (
                      <span className="font-semibold">{category.name}</span>
                    )}
                    optionItemSkin={(category) => category.name}
                  />
                  {errors && (
                    <ErrorMessage
                      errors={errors}
                      name="category"
                      render={({ message }) => (
                        <ErrorMessageStyle message={message} />
                      )}
                    />
                  )}
                </Label>
              )}
            />

            <div>
              <TextAreaBasic
                register={register}
                label="Description"
                placeholder="Write about your problem..."
                errors={errors}
                name="description"
              />
            </div>
            <MultiFileUploader ref={uploaderRef} />
          </div>
        }
        footer={
          <div className="mt-4 flex gap-4 text-sm font-semibold p-2">
            <button
              onClick={closePopup}
              className="flex-1 bg-white text-gray-700 border border-gray-700  px-3 py-2 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || isUploadingDocuments}
              className="flex-1 flex justify-center items-center bg-green text-white px-3 py-2 rounded-xl"
            >
              <span> Send</span>

              {(isLoading || isUploadingDocuments) && (
                <div className="ml-2">
                  <Spinner bounce={false} />
                </div>
              )}
            </button>
          </div>
        }
      ></OverlayWindow>
    </form>
  );
}
